import { useEffect, useState } from 'react';

import { CycloneDXVersion, SPDXVersion } from '../../../../../shared/model-typings/sbomrule';
import { useAddToast } from '../../../../contexts/ToastContext';
import useEditable from '../../../../hooks/useEditable';
import { put } from '../../../../redux/api';
import type { SBOMRule } from '../../../../../shared/model-typings/sbomrule';
import type { SBOMPolicy } from '../../../../../typings/api';

/**
 * Custom hook to manage the state and logic related to an SBOM policy rule.
 *
 * @returns {object} - An object containing:
 *   SBOMRule rule - The current SBOM policy rule.
 *   isDirty - Boolean flag indicating if the current rule has been modified.
 *   handleUpdate - Function to update the SBOM policy rule with new values.
 *   handleSavePolicyRule - Function to save the SBOM policy rule with new values.
 */
const useSBOMPolicyRule = (policy: SBOMPolicy) => {
  const [initRule, setInitRule] = useState<SBOMRule>({} as SBOMRule);
  const addToast = useAddToast();

  useEffect(() => {
    const sbomRule = policy.rules ? policy?.rules[policy.rules.length - 1] : null;
    if (sbomRule) {
      setInitRule(sbomRule);
    }
  }, [policy, setInitRule]);

  const {
    data: rule,
    isDirty,
    onUpdate: handleUpdate,
    onSave,
  } = useEditable(initRule);

  const handleSavePolicyRule = (async () => {
    if (!rule.ruleId) {
      addToast({
        type: 'error',
        content: 'Oops! There was an error. Rule ID not found.',
      });
      throw new Error('Missing Rule ID');
    }

    if (rule.minCycloneDXVersion === CycloneDXVersion.DENIED && rule.minSPDXVersion === SPDXVersion.DENIED) {
      addToast({
        type: 'error',
        content: 'At least one supported format must be required. Both formats can be required.',
      });
      return;
    }

    try {
      const result = await put<SBOMRule, { rules: SBOMRule[] }>(`/api/policies/${policy.id}/rules/${rule.ruleId}`, rule);
      setInitRule(result?.data?.rules[result.data.rules.length - 1] as SBOMRule);
      onSave();

      addToast({
        type: 'success',
        content: 'SBOM rule successfully saved!',
      });
    } catch (error: unknown) {
      const actualError = error as { data: { message: string } };
      const errorMessage = actualError?.data?.message;
      addToast({
        type: 'error',
        content: errorMessage,
      });
    }
  });

  return {
    rule,
    isDirty,
    handleUpdate,
    handleSavePolicyRule,
  };
};

export default useSBOMPolicyRule;
